/**
 * FeedbackPage — standalone public page for the emailed Front Window Survey.
 *
 * Accessible via /feedback/:transactionLogId (no authentication required today;
 * the transactionLogId acts as the person-scoped access key).
 *
 * In a future iteration this can be upgraded to use a real service-member login
 * once service-member permissions are implemented.
 */
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Paper, Typography } from '@mui/material';
import { CustomerFeedbackModal } from '@/components/feedback/CustomerFeedbackModal';

export const FeedbackPage: React.FC = () => {
  const { transactionLogId } = useParams<{ transactionLogId: string }>();
  const [open, setOpen] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = () => {
    // Closing via "Skip" inside the embedded modal just closes — no re-routing.
    setOpen(false);
  };

  return (
    <Container maxWidth="sm" sx={{ py: 6 }}>
      <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
        {submitted ? (
          <>
            <Typography variant="h5" gutterBottom>
              Thank you!
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Your survey response has been recorded.
            </Typography>
          </>
        ) : open ? (
          <>
            <Typography variant="h5" gutterBottom>
              Customer Service Survey
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please take a moment to complete your feedback form.
            </Typography>
            {/* Render the modal inline — keep the same form/API as the in-app flow */}
            <CustomerFeedbackModal
              open={open}
              transactionLogId={transactionLogId ?? null}
              onClose={handleClose}
              onSubmitSuccess={() => {
                setSubmitted(true);
                setOpen(false);
              }}
            />
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Survey closed
            </Typography>
            <Typography variant="body2" color="text.secondary">
              You can re-open this link at any time to complete the survey.
            </Typography>
          </>
        )}
      </Paper>
    </Container>
  );
};
